import type { DateInput } from "@/lib/time"
import { DAY_MS } from "@/lib/time"

export type MeetingStatusLabel =
  | { kind: "live" }
  | { kind: "minutes"; count: number }
  | { kind: "hours"; count: number }
  | { kind: "tomorrow" }
  | { kind: "days"; count: number }
  | { kind: "months"; count: number }

export type MeetingStatusTone = "live" | "urgent" | "warning" | "future"

export const getMeetingEndTimestamp = (
  startAt: DateInput,
  durationMinutes: number,
): number => new Date(startAt).getTime() + durationMinutes * 60_000

export const hasMeetingEnded = (
  startAt: DateInput,
  durationMinutes: number,
  referenceTime = Date.now(),
): boolean => referenceTime >= getMeetingEndTimestamp(startAt, durationMinutes)

const createStartOfDay = (date: Date) => {
  const nextDate = new Date(date)
  nextDate.setHours(0, 0, 0, 0)
  return nextDate
}

export const getMeetingStatus = (
  startAt: DateInput,
  durationMinutes: number,
): MeetingStatusLabel | null => {
  const now = Date.now()
  const start = new Date(startAt).getTime()
  const end = getMeetingEndTimestamp(startAt, durationMinutes)
  const startDate = new Date(startAt)

  if (now >= start && now < end) {
    return { kind: "live" }
  }

  if (now >= end) {
    return null
  }

  const diffMs = start - now
  const totalMinutesUntilStart = Math.max(1, Math.ceil(diffMs / 60_000))
  const calendarDayDifference = Math.round(
    (createStartOfDay(startDate).getTime() -
      createStartOfDay(new Date(now)).getTime()) /
      DAY_MS,
  )

  if (totalMinutesUntilStart < 60) {
    return { kind: "minutes", count: totalMinutesUntilStart }
  }

  if (diffMs < DAY_MS) {
    return { kind: "hours", count: Math.floor(totalMinutesUntilStart / 60) }
  }

  if (calendarDayDifference === 1) {
    return { kind: "tomorrow" }
  }

  if (calendarDayDifference < 30) {
    return { kind: "days", count: calendarDayDifference }
  }

  const months = Math.floor(calendarDayDifference / 30)
  return { kind: "months", count: months }
}

export const getMeetingStatusText = (
  status: MeetingStatusLabel,
  t: TFunction,
): string => {
  switch (status.kind) {
    case "live":
      return t("meetingStatus.liveNow")
    case "minutes":
      return t("meetingStatus.inMinutes", {
        count: status.count,
      })
    case "hours":
      return t("meetingStatus.inHours", {
        count: status.count,
      })
    case "tomorrow":
      return t("common.tomorrow")
    case "days":
      return t("meetingStatus.inDays", {
        count: status.count,
      })
    case "months":
      return status.count >= 12
        ? t("meetingStatus.inMonthsPlus")
        : t("meetingStatus.inMonths", {
            count: status.count,
          })
  }
}

export const getMeetingStatusTone = (
  status: MeetingStatusLabel,
): MeetingStatusTone => {
  switch (status.kind) {
    case "live":
      return "live"
    case "minutes":
    case "hours":
      return "urgent"
    case "tomorrow":
      return "warning"
    case "days":
    case "months":
      return "future"
  }
}
import type { TFunction } from "i18next"
